import { Link } from "@tanstack/react-router";
import { ChevronRight, Images, Store } from "lucide-react";

import { DesktopShell } from "../../components/desktop/shell.tsx";
import { DesktopProductCard } from "../../components/desktop/product-card.tsx";
import { SpaceIcon } from "../../components/space-icon.tsx";
import { useSpaces } from "../../lib/site-config.ts";
import { Empty, ErrorBlock, Img, Price, Spinner } from "../../components/ui.tsx";
import { useCases, useCategories, useIdentities, useMe, useProducts } from "../../api/queries.ts";

/**
 * ═══════════════════════════════════════════════════════════════
 *  网页版 · 首页
 * ═══════════════════════════════════════════════════════════════
 *
 * 🔴 **网页首屏能同时放下"逛什么"和"我是谁"。**
 *    手机版是一条条卡片竖着堆（搜索 → 空间 → 案例 → 品类 → 热销），
 *    一屏只放得下两三块。网页版把这些**并排**：
 *    左边品类直达（常驻，像超市的货架索引），中间按空间逛的大图入口，
 *    右边"我的身份"卡片（我的店 / 我的专属服务商 / 案例）。
 *
 * ⚠️ 「按空间逛」仍然排在品类前面 —— 买建材的人想的是"我家客厅要装什么"，
 *    而不是"我要买瓷砖"，后者是已经想清楚的人才有的念头。这条跟手机版一致，
 *    因为它是关于**人怎么想**的，不是关于屏幕多宽。
 */

export function DesktopHome() {
  const { data: me } = useMe();
  const { data: identities } = useIdentities(Boolean(me));
  const { data: categories } = useCategories();
  const { data, isPending, error } = useProducts({ sort: "sales", limit: 8 });
  const cases = useCases();
  /** 🔴 后台配的那一排，跟手机版同一份数据。 */
  const spaces = useSpaces();

  /** 我同时是商家时给个入口。⚠️ 一个人既是买家又是分销商是常态，不是特例。 */
  const merchantOrgs = identities?.orgs.filter((o) => o.status === "active") ?? [];
  /** 我作为买家的专属服务商。⚠️ 跟"我是商家"是两回事，一个人可以两样都有。 */
  const myShop = identities?.binding ?? null;

  return (
    <DesktopShell>
      {/* ══════════ 首屏：品类索引 + 按空间逛 + 我的身份 ══════════ */}
      <div className="grid grid-cols-[200px_minmax(0,1fr)_280px] items-start gap-6">
        {/*
          ⚠️ 品类做成**竖排直达链接**而不是手机版那堆圆角标签 ——
             网页上鼠标扫一列比在一团标签里找快得多，这也是电商站的通行做法。
        */}
        <aside className="rounded-xl border border-border bg-card py-2">
          <p className="px-4 py-2 text-[13px] font-medium text-muted-foreground">全部品类</p>
          {(categories ?? []).slice(0, 12).map((c) => (
            <Link
              key={c.id}
              to="/products"
              search={{ categoryId: c.id } as never}
              className="flex items-center justify-between px-4 py-2 text-[14px] text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
            >
              {c.name}
              <ChevronRight className="size-3.5 opacity-40" />
            </Link>
          ))}
          <Link
            to="/products"
            search={{} as never}
            className="block px-4 pt-2 pb-1 text-[13px] text-brand-600 hover:text-brand-700"
          >
            查看全部 →
          </Link>
        </aside>

        {/* ── 中：按空间逛 ── */}
        <section className="overflow-hidden rounded-xl bg-gradient-to-br from-brand-600 to-brand-500 p-7">
          <p className="text-[13px] text-white/75">装修建材 · 一站购齐</p>
          <h1 className="mt-1.5 text-[28px] leading-tight font-medium text-white">
            按你家的空间挑
          </h1>
          {/*
            ⚠️ **这里不讲平台怎么定价。** 早先写的是"中间每一级的价都算得清清楚楚"——
               那是在跟客户描述分销结构，等于把运营模式摆出来。对外只说他要什么。
          */}
          <p className="mt-1.5 text-[14px] text-white/80">
            瓷砖、地板、卫浴、灯具 —— 装一套房要的建材，按房间挑就行
          </p>

          {/*
            ⚠️ **列数跟着后台配了几格走，别写死 5。** 写死的话他在后台停用一格，
               这一排就空出一个洞；加一格又会被挤到第二行孤零零站一个。
          */}
          <div
            className="mt-6 grid gap-3"
            style={{ gridTemplateColumns: `repeat(${Math.max(spaces.length, 1)}, minmax(0, 1fr))` }}
          >
            {spaces.map((s) => (
              <Link
                key={s.key}
                to="/products"
                search={{ space: s.key } as never}
                className="rounded-xl bg-white/15 p-3.5 text-center backdrop-blur transition-colors hover:bg-white/25"
              >
                <SpaceIcon icon={s.icon} size={28} className="mx-auto text-white" />
                <span className="mt-2 block text-[14px] font-medium text-white">{s.label}</span>
                {/* ⚠️ 网页上有空间写一行说明 —— 手机上那五个图标只能光秃秃摆着。 */}
                <span className="mt-0.5 block text-[11px] leading-tight text-white/70">
                  {s.hint}
                </span>
              </Link>
            ))}
          </div>
        </section>

        {/* ── 右：我的身份 ── */}
        <aside className="space-y-3">
          {merchantOrgs.length > 0 && (
            <Link
              to="/merchant"
              search={{} as never}
              className="flex items-center gap-3 rounded-xl border border-brand-200 bg-brand-50 px-4 py-3.5 transition-colors hover:bg-brand-100/70"
            >
              <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-brand-600 text-white">
                <Store className="size-[18px]" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[14px] font-medium text-brand-700">
                  {merchantOrgs[0]!.name}
                </span>
                <span className="block text-[12px] text-brand-600/80">
                  {merchantOrgs.length > 1
                    ? `等 ${merchantOrgs.length} 个商家身份 · 去经营`
                    : "去我的店里看看"}
                </span>
              </span>
              <ChevronRight className="size-4 shrink-0 text-brand-600" />
            </Link>
          )}

          {/*
            🔴 **我的专属服务商。**
               这是这个平台跟普通商城最不一样的一条：买家有一个固定的服务商，
               价格按他给你算、售后找他。不放在首页的话用户根本不知道有这回事，
               而这恰恰是他能享受到的好处。
          */}
          {myShop && (
            <Link
              to="/shop/$id"
              params={{ id: String(myShop.id) }}
              className="flex items-center gap-3 rounded-xl border border-brand-200 bg-brand-50 px-4 py-3.5 transition-colors hover:bg-brand-100/70"
            >
              <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-brand-600">
                <Store className="size-[18px] text-white" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[14px] font-medium text-brand-700">
                  {myShop.name}
                </span>
                <span className="block text-[12px] text-brand-600/80">
                  你的专属服务商 · 比平台价便宜
                </span>
              </span>
              <ChevronRight className="size-4 shrink-0 text-brand-600" />
            </Link>
          )}

          {!me && (
            /*
              🔴 **不能把定价机制说给游客听。**
                 早先这里写的是"现在看到的是平台指导价 / 从相熟的商家店里进来、
                 或登录后绑定服务商，价格会更低"—— 那是平台的运营模式，
                 而且等于当面告诉客户"你现在被收的是最贵的一档"。
                 只说他登录之后能得到什么，不解释是怎么算出来的。
            */
            <div className="rounded-xl border border-border bg-card p-4">
              <p className="text-[14px] font-medium">登录后查看你的专属价</p>
              <p className="mt-1 text-[13px] leading-relaxed text-muted-foreground">
                注册后会有专门的人负责你的选材、报价和售后。
              </p>
              <Link
                to="/login"
                className="mt-3 inline-flex h-9 items-center rounded-lg bg-brand-600 px-4 text-[13px] font-medium text-white transition-colors hover:bg-brand-700"
              >
                登录 / 注册
              </Link>
            </div>
          )}

          <Link
            to="/cases"
            search={{} as never}
            className="flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-3.5 transition-colors hover:border-brand-200"
          >
            <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-secondary">
              <Images className="size-[18px] text-muted-foreground" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-[14px] font-medium">装修案例</span>
              <span className="block text-[12px] text-muted-foreground">看看别人家怎么装的</span>
            </span>
            <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
          </Link>
        </aside>
      </div>

      {/* ══════════ 热销 ══════════ */}
      <section className="mt-10">
        <div className="mb-4 flex items-end justify-between">
          <h2 className="text-[20px] font-medium">热销建材</h2>
          <Link
            to="/products"
            search={{ sort: "sales" } as never}
            className="text-[14px] text-muted-foreground transition-colors hover:text-foreground"
          >
            查看全部 <ChevronRight className="inline size-3.5 -translate-y-px" />
          </Link>
        </div>

        {isPending ? (
          <Spinner rows={2} />
        ) : error ? (
          <ErrorBlock error={error} />
        ) : (data?.items.length ?? 0) === 0 ? (
          <Empty title="还没有上架的商品" hint="平台正在招商，很快就有了" compact />
        ) : (
          /* ⚠️ 一行四张、两行八件 —— 首页给个"看得过来"的量，想多看点「查看全部」。 */
          <div className="grid grid-cols-4 gap-5">
            {(data?.items ?? []).map((p) => (
              <DesktopProductCard key={p.id} p={p} myShopName={myShop?.name ?? null} />
            ))}
          </div>
        )}
      </section>

      {/* ══════════ 装修案例 ══════════ */}
      {(cases.data?.items.length ?? 0) > 0 && (
        <section className="mt-10">
          <div className="mb-4 flex items-end justify-between">
            <div>
              <h2 className="text-[20px] font-medium">装修案例</h2>
              {/* 建材是低频高客单，用户往往不知道"我家该买什么" ——
                  把商品放进一个具体的家里，比商品列表更容易让人下决心。 */}
              <p className="mt-1 text-[13px] text-muted-foreground">
                每套案例里的建材都能直接买，价格按你的服务商现算
              </p>
            </div>
            <Link
              to="/cases"
              search={{} as never}
              className="text-[14px] text-muted-foreground transition-colors hover:text-foreground"
            >
              查看全部 <ChevronRight className="inline size-3.5 -translate-y-px" />
            </Link>
          </div>

          <div className="grid grid-cols-3 gap-5">
            {cases.data!.items.slice(0, 3).map((c) => (
              <Link
                key={c.id}
                to="/cases/$slug"
                params={{ slug: c.slug }}
                className="group overflow-hidden rounded-xl border border-border bg-card transition-all hover:border-brand-200 hover:shadow-[0_2px_16px_rgba(0,0,0,0.06)]"
              >
                <div className="aspect-[16/10] overflow-hidden bg-secondary">
                  <Img
                    src={c.coverUrl}
                    alt={c.title}
                    className="size-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                  />
                </div>
                <div className="p-4">
                  <p className="line-clamp-1 text-[15px]">{c.title}</p>
                  <p className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-muted-foreground">
                    <span>{c.style}</span>
                    {c.areaSqm && <span>{c.areaSqm} ㎡</span>}
                    {c.budget != null && (
                      <span>
                        预算 <Price cents={c.budget} />
                      </span>
                    )}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </DesktopShell>
  );
}
